import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  localScaleRegisterKey, localScaleScopeKey, readLocalScalePreference,
  rememberLocalScalePort, saveLocalScalePreference, subscribeLocalScalePreference,
} from '../utils/localScalePreferences';
import { createLocalScaleTransport, localScaleSupported } from '../utils/localScaleTransport';

export default function useLocalScale({ companyId, branchId, cashRegisterId, cashRegisterSessionId, userId, enabled, isEditor }) {
  const registerKey = localScaleRegisterKey({ companyId, branchId, cashRegisterId });
  const scopeKey = localScaleScopeKey({ companyId, branchId, cashRegisterId, cashRegisterSessionId, userId });
  const [version, setVersion] = useState(0);
  const [snapshot, setSnapshot] = useState(null);
  const preference = useMemo(() => readLocalScalePreference(registerKey), [registerKey, version]);
  const current = useRef(null);
  current.current = enabled && isEditor && scopeKey ? { registerKey, scopeKey } : null;
  const supported = localScaleSupported();

  useEffect(() => subscribeLocalScalePreference(registerKey, () => setVersion(value => value + 1)), [registerKey]);
  useEffect(() => {
    current.current = enabled && isEditor && scopeKey ? { registerKey, scopeKey } : null;
    return () => { current.current = null; };
  }, [enabled, isEditor, registerKey, scopeKey]);
  useEffect(() => {
    if (!enabled || preference.mode !== 'local' || !scopeKey) { setSnapshot(null); return undefined; }
    if (!supported) {
      setSnapshot({ scopeKey, state: { enabled: true, status: 'disconnected', connectionStatus: 'disconnected', lastError: 'La balanza local requiere Chrome o Edge en una conexión HTTPS.' } });
      return undefined;
    }
    let mounted = true;
    const transport = createLocalScaleTransport({
      scopeKey, registerKey, preference, isEditor,
      onState: state => { if (mounted) setSnapshot({ scopeKey, state }); },
    });
    const stop = () => { mounted = false; transport.stop(); };
    // A restored bfcache page must remount its transport rather than retain a
    // closed channel. POS pages normally reload; pageshow handles that case too.
    const onPageShow = event => { if (event.persisted) setVersion(value => value + 1); };
    window.addEventListener('pagehide', stop);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pagehide', stop);
      window.removeEventListener('pageshow', onPageShow);
      stop();
    };
  }, [enabled, isEditor, preference, registerKey, scopeKey, supported]);

  const setConnectionMode = useCallback(mode => {
    if (!current.current || current.current.registerKey !== registerKey) throw new Error('Abre una sesión propia en la pestaña POS para configurar la balanza.');
    if (mode === 'local' && !supported) throw new Error('Usa Chrome o Edge con HTTPS para conectar la balanza local.');
    saveLocalScalePreference(registerKey, { ...readLocalScalePreference(registerKey), mode });
  }, [registerKey, supported]);

  const connectLocalScale = useCallback(async (options = {}) => {
    const request = current.current;
    if (!request || request.registerKey !== registerKey || !supported) throw new Error('Abre una sesión propia en Chrome o Edge para conectar la balanza local.');
    // requestPort must run directly in the button's user gesture.
    const port = await navigator.serial.requestPort();
    if (current.current?.scopeKey !== request.scopeKey) throw new Error('La sesión cambió. Selecciona la balanza desde la caja activa.');
    const value = { ...readLocalScalePreference(registerKey), ...options, mode: 'local', device: port.getInfo() };
    saveLocalScalePreference(registerKey, value);
    rememberLocalScalePort(registerKey, port);
  }, [registerKey, supported]);

  return {
    connectionMode: preference.mode,
    localScalePreference: preference,
    localScaleSupported: supported,
    connectLocalScale,
    setConnectionMode,
    localState: enabled && snapshot?.scopeKey === scopeKey ? snapshot.state : null,
  };
}
